import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const InviteUser = () => {
  const { registeredUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [firstName, setFirstName]     = useState('');
  const [lastName, setLastName]       = useState('');
  const [email, setEmail]             = useState('');
  const [firms, setFirms]             = useState([]);          // available firms from API
  const [selectedFirms, setSelectedFirms] = useState(['all']); // default: All Firms
  const [loading, setLoading]         = useState(false);

  useEffect(() => {
    // ProtectedRoute adminOnly already guards this page — secondary check
    if (!registeredUser?.isAdmin) {
      navigate('/trader-dashboard', { replace: true });
      return;
    }

    // Fetch available firms
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/trader-firms`, { withCredentials: true })
      .then(res => setFirms(res.data))
      .catch(() => toast.error("Failed to load firms."));
  }, [registeredUser, navigate]);

  const toggleFirm = (slug) => {
    if (slug === 'all') {
      // Toggle All: if already selected → deselect everything; if not → select All
      setSelectedFirms(prev => prev.includes('all') ? [] : ['all']);
      return;
    }
    // Individual firm clicked — always remove 'all', then toggle just this firm
    setSelectedFirms(prev => {
      const withoutAll = prev.filter(s => s !== 'all');
      return withoutAll.includes(slug)
        ? withoutAll.filter(s => s !== slug)   // deselect — no fallback to 'all'
        : [...withoutAll, slug];               // select
    });
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !email) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (selectedFirms.length === 0) {
      toast.error("Please select at least one firm.");
      return;
    }

    // Resolve 'all' to every firm slug
    const firmSlugs = selectedFirms.includes('all')
      ? firms.map(f => f.firm_slug)
      : selectedFirms;

    if (firmSlugs.length === 0) {
      toast.error("No firms available. Cannot send invite.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/tradeinvite-user`,
        { firstName, lastName, email, firmSlugs },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Invitation sent successfully!");
        setFirstName('');
        setLastName('');
        setEmail('');
        setSelectedFirms(['all']);
      } else {
        toast.error(res.data.message || "Failed to invite user.");
      }
    } catch (err) {
      console.error("Invite error:", err);
      toast.error("Server error while sending invite.");
    } finally {
      setLoading(false);
    }
  };

  const isAllSelected = selectedFirms.includes('all');

  return (
    <div className="login-form">
      <h2>Invite New User</h2>
      <form onSubmit={handleInvite}>
        <input
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        {/* Firm access selection */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
            Firm Access
          </p>

          {/* All Firms option */}
          <label style={checkboxLabelStyle(isAllSelected)}>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={() => toggleFirm('all')}
              style={{ marginRight: '8px' }}
            />
            All Firms
          </label>

          {/* Individual firm options */}
          {firms.map(firm => {
            const checked = isAllSelected || selectedFirms.includes(firm.firm_slug);
            return (
              <label
                key={firm.firm_slug}
                style={checkboxLabelStyle(checked && !isAllSelected)}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleFirm(firm.firm_slug)}
                  style={{ marginRight: '8px' }}
                />
                {firm.display_name || firm.firm_slug}
              </label>
            );
          })}
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Sending...' : 'Send Invite'}
        </button>
      </form>
    </div>
  );
};

// Helper for checkbox row styles
function checkboxLabelStyle(active) {
  return {
    display       : 'flex',
    alignItems    : 'center',
    padding       : '8px 12px',
    marginBottom  : '6px',
    borderRadius  : '6px',
    border        : `1px solid ${active ? '#7c3aed' : '#e5e7eb'}`,
    background    : active ? '#f5f3ff' : '#fff',
    color         : '#111827',
    cursor        : 'pointer',
    fontSize      : '14px',
    transition    : 'border 0.15s, background 0.15s',
  };
}

export default InviteUser;
